/**
 * Room presence — the authoritative roster of who is in the meeting.
 *
 * Phoenix Presence (tracked server-side in RoomChannel) is the source of truth
 * for *which peers are in the room* and their *display name / video flag*. It is
 * independent of media: a peer shows up here the moment they join the channel,
 * even before (or without) any SFU producer/consumer.
 *
 * We consume two channel events:
 *   - `presence_state` — full snapshot, pushed right after we join.
 *   - `presence_diff`  — incremental `{ joins, leaves }`.
 *
 * The Map's order is the seat order the grid draws in. How that order is kept
 * stable — why a camera toggle or a reconnect never moves anyone — lives in
 * services/presence-roster, where it is tested.
 *
 * Keyed by peerId (= Phoenix `user_id` = JWT `sub` = our SFU peerId), so the
 * map lines up 1:1 with consumer peerIds for stream attachment downstream.
 */

import { useEffect, useRef, useState } from 'react'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { vclog } from './vc-log'
import {
  applyPresenceDiff,
  applyPresenceState,
  EMPTY_ROSTER,
  toPeers,
  type PeerPresence,
  type PresenceDiff,
  type PresenceEntry,
  type PresenceRoster,
} from '../services/presence-roster'

export type { PeerPresence } from '../services/presence-roster'

const NO_PEERS: ReadonlyMap<string, PeerPresence> = new Map()

/**
 * @param roomKey Identity of the room channel (its topic). The roster belongs
 *   to one room: when this changes — moving into or out of a breakout room —
 *   the previous room's people are forgotten rather than left on screen until
 *   the new room's snapshot lands. A reconnect to the SAME room keeps the
 *   roster, and the snapshot that follows re-confirms it seat for seat.
 */
export function usePresence(
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void,
  roomKey: string | null,
): ReadonlyMap<string, PeerPresence> {
  const [peers, setPeers] = useState<ReadonlyMap<string, PeerPresence>>(NO_PEERS)
  // Authoritative working copy — diffs apply to this without waiting on a render.
  const rosterRef = useRef<PresenceRoster>(EMPTY_ROSTER)

  // Derived-state reset, during render, so no frame shows the old room.
  const [boundRoom, setBoundRoom] = useState(roomKey)
  if (boundRoom !== roomKey) {
    setBoundRoom(roomKey)
    setPeers(NO_PEERS)
  }

  useEffect(() => {
    rosterRef.current = EMPTY_ROSTER

    const publish = (roster: PresenceRoster) => {
      if (roster === rosterRef.current) return
      rosterRef.current = roster
      setPeers((prev) => toPeers(roster, prev))
    }

    const offState = subscribe('presence_state', (payload) => {
      const snapshot = payload as Record<string, PresenceEntry>
      vclog('presence', 'presence_state (full snapshot)', { keys: Object.keys(snapshot) })
      publish(applyPresenceState(rosterRef.current, snapshot))
    })

    const offDiff = subscribe('presence_diff', (payload) => {
      const diff = payload as PresenceDiff
      const joinKeys = Object.keys(diff.joins ?? {})
      const leaveKeys = Object.keys(diff.leaves ?? {})
      if (joinKeys.length || leaveKeys.length) {
        vclog('presence', 'presence_diff', { joins: joinKeys, leaves: leaveKeys })
      }
      publish(applyPresenceDiff(rosterRef.current, diff))
    })

    return () => {
      offState()
      offDiff()
    }
  }, [subscribe, roomKey])

  return peers
}
